import React, { useContext, useState } from 'react';

import './SeasonPassPersonalInfo.sass';
import styles from './SeasonPassPersonalInfo.module.sass';
import Icon from '../../../components/UI/Icon/Icon';

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { EditMembershipCardInfoModal } from './Modals/EditMembershipCardInfo';
import { getCurrentLanguageTexts } from '../../../constants/seasonPassPersonalInfoLangs';
import { BuySeasonPassModal } from './Modals/BuySeasonPassModal';
import { StatusLabel } from '../../../components/Transactions/StatusLabel/StatusLabel';

import axios from "../../../axios-instance";
import NewLoader from '../../../components/NewLoader/NewLoader';
import { Tooltip } from '../../../components/UI/Tooltip/Tooltip';
import { toast } from 'react-toastify';
import ls from 'localstorage-slim';
import { MemberPageContext } from './MemberPage';
import { BuyMembershipsModalNew } from './Modals/BuyMembershipModal';
import { MoveSeatModal } from "./Modals/MoveSeatModal";
import { IconButton } from '../../../components/UI/IconButton/IconButton';
import { useShowActionsMenu } from '../../../components/UI/ActionsMenu/hooks/useShowActionsMenu';
import { SelectAnotherHoldedSeatModal } from './Modals/SelectAnotherHoldedSeatModal';
import { ChangeSPSeatModal } from './Modals/ChangeSPSeatModal';

const MySwal = withReactContent(Swal);

export const MembershipCard = ({ onSave, membershipCard, currencyLabel, onConfirmBuySeasonPass, availablePoints, eventInfo, allowChangeHoldedSeat, ableToBuyNFC, holdedSeats }) => {
    const txt = getCurrentLanguageTexts();
    const memberPageContext = useContext(MemberPageContext);

    const [showMenuActions] = useShowActionsMenu();

    const [allowResendTicket, setAllowResendTicket] = useState(!!membershipCard.transactionId);

    const hasSeat = !!membershipCard.seat;
    const isLoading = ('loading' in membershipCard);
    const allowEdit = !membershipCard.hasSeasonPass && !isLoading;
    const allowBuy = membershipCard.allowBuySeasonPass;

    const showEditMembershipCardInfo = () => {
        if (!allowEdit) {
            return;
        }

        MySwal.fire({
            html: (
                <EditMembershipCardInfoModal
                    membershipCard={membershipCard}
                    editMembeshipCardInfo={onSave}
                    eventInfo={eventInfo}
                    title={txt.EditMembershipCardInfoModalTxt['title']}
                />
            ),
            showConfirmButton: false,
            showCloseButton: true,
            customClass: "public-user-modal member-page-modal",
        });
    };

    const showUpgradeSeatModal = () => {
        if (!membershipCard.allowUpgradeSeasonPass) {
            return;
        }

        const requests = [];

        const getSeatLabel = (ticket) => {
            return ticket.area + (!!ticket.side ? `-${ticket.side}` : '') + `-${ticket.row}-${ticket.seat}`
        }

        requests.push(axios.get(`TicketTemplate/${process.env.REACT_APP_NEW_SEASON_EVENT_ID}/${getSeatLabel(membershipCard)}?hasSide=${!!membershipCard.side}`),
            axios.get(`EventPublicInfo/BySlug/${process.env.REACT_APP_NEW_SEASON_EVENT_SLUG}`));

        Promise.all(requests)
            .then((responses) => {
                const isCashPaymentMode = ls.get('pageAction') === 'selling';

                if (isCashPaymentMode) {
                    responses[1].data.feeRate = 0;
                    responses[1].data.fixedFeeRate = 0;
                }

                MySwal.fire({
                    html: (
                        <ChangeSPSeatModal
                            membershipCard={membershipCard}
                            memberData={memberPageContext.userData}
                            event={responses[1].data}
                            ticketTemplate={responses[0].data}
                            title={txt.ChangeSPSeatModalTxt['title']}
                        />
                    ),
                    showConfirmButton: false,
                    showCloseButton: true,
                    customClass: "public-user-modal member-page-modal popup-mode",
                });
            });
    };

    const showBuySeasonPassModal = (onlyNfcPayment = false) => {
        if ((membershipCard.hasSeasonPass || !allowBuy) && !onlyNfcPayment) {
            return;
        }

        const requests = [];

        const getSeatLabel = (ticket) => {
            return ticket.area + (!!ticket.side ? `-${ticket.side}` : '') + `-${ticket.row}-${ticket.seat}`
        }

        requests.push(axios.get(`TicketTemplate/${process.env.REACT_APP_NEW_SEASON_EVENT_ID}/${getSeatLabel(membershipCard)}?hasSide=${!!membershipCard.side}`),
            axios.get(`EventPublicInfo/BySlug/${process.env.REACT_APP_NEW_SEASON_EVENT_SLUG}`),
            axios.get(`HoldedItems/AllowBuySP/${membershipCard.id}`));

        Promise.all(requests)
            .then((responses) => {
                if(!responses[2].data)
                {
                    toast.error(txt.BuySeasonPassModalTxt.seatExpiredError);
                    return;
                }

                const isCashPaymentMode = ls.get('pageAction') === 'selling';

                if (isCashPaymentMode) {
                    responses[1].data.feeRate = 0;
                    responses[1].data.fixedFeeRate = 0;
                }

                MySwal.fire({
                    html: (
                        <BuySeasonPassModal
                            membershipCard={membershipCard}
                            ticketTemplate={responses[0].data}
                            availablePoints={availablePoints}
                            onConfirmBuySeasonPass={onConfirmBuySeasonPass}
                            onlyNfcPayment={onlyNfcPayment}
                            event={responses[1].data}
                            title={onlyNfcPayment ? txt.BuySeasonPassModalTxt['titleNfcOnly'] : txt.BuySeasonPassModalTxt['title']}
                        />
                    ),
                    showConfirmButton: false,
                    showCloseButton: true,
                    customClass: "public-user-modal member-page-modal",
                });
            })
    }

    const resendTicket = async () => {
        const token = ls.get('member-access-token')

        if (!token) return;

        try {
            setAllowResendTicket(false);

            const result = await axios.post(
                `SeasonPassMember/ResendTicket?transactionId=${membershipCard.transactionId}&accessToken=${token}`
            );

            if (result) {
                toast.success(txt.ResentEmail.success)
            } else {
                toast.error(txt.ResentEmail.error)
            }
        } catch (error) {
            toast.error(txt.ResentEmail.error)
        } finally {
            setAllowResendTicket(true)
        }
    }

    const moveSeatToEmail = () => {
        MySwal.fire({
            title: txt.MoveHoldedSeatModalTxt['title'],
            html: (
                <MoveSeatModal
                    seatLabel={`${membershipCard.area}-${membershipCard.side}-${membershipCard.row}-${membershipCard.seat}`} />
            ),
            showConfirmButton: false,
            showCloseButton: true,
            customClass: "public-user-modal member-page-modal",
        });
    };

    const selectAnotherHoldedSeat = () => {
        MySwal.fire({
            title: txt.SelectAnotherHoldedSeatModalTxt['title'],
            html: (
                <SelectAnotherHoldedSeatModal
                    membershipId={membershipCard.id}
                    seatLabel={`${membershipCard.area}-${membershipCard.side}-${membershipCard.row}-${membershipCard.seat}`}
                    holdedSeats={holdedSeats} />
            ),
            showConfirmButton: false,
            showCloseButton: true,
            customClass: "public-user-modal member-page-modal",
        });
    };

    const menuItems = [];

    if (!membershipCard.hasSeasonPass) {
        if (allowEdit) {
            menuItems.push({
                text: txt.EditMembershipCardInfoModalTxt.title,
                onClick: showEditMembershipCardInfo,
                className: styles['menu-item']
            });
        }

        if (membershipCard.hasDefaultSeat) {
            menuItems.push({
                text: txt.MoveHoldedSeatModalTxt.title,
                onClick: moveSeatToEmail,
                className: styles['menu-item']
            });
        }

        if (!!holdedSeats && holdedSeats.length) {
            menuItems.push({
                text: txt.SelectAnotherHoldedSeatModalTxt.selectAnotherHoldedSeat,
                onClick: selectAnotherHoldedSeat,
                className: styles['menu-item']
            });
        }
    }

    if (membershipCard.hasSeasonPass) {
        if (allowResendTicket) {
            menuItems.push({
                text: txt.SeasonPassListTxt['toolTipResend'],
                onClick: resendTicket,
                className: styles['menu-item']
            });
        }

        if (membershipCard.allowUpgradeSeasonPass) {
            menuItems.push({
                text: txt.SeasonPassListTxt['changeSeat'],
                onClick: showUpgradeSeatModal,
                className: styles['menu-item']
            });
        }
    }

    const menuOptions = {
        items: menuItems,
        minMenuHeight: 35 * menuItems.length,
        offsetY: 0,
    };

    return <>
        <li className={styles['membership-card']}>

            <div className={styles['membership-card-edit']}>

                <div className={styles['ticket-and-price']}>

                    <div>
                        <p className="partizan-open-selling__text-normal-small">{membershipCard.ticketName}</p>
                        <p className={styles['price-label'] + " partizan-open-selling__text-bold"}>{membershipCard.ticketPrice} {currencyLabel}</p>
                    </div>

                    <div className={"buttons-block " + styles['edit-icon']}>
                        <IconButton
                            iconName={'options'}
                            className={'btn-primary IconButton primary trigger-actions-menu'}
                            onClick={showMenuActions(menuOptions)}
                        />
                    </div>

                </div>

                {membershipCard.customerName &&
                    <p>{txt.SeasonPassListTxt['for']} {membershipCard.customerName}</p>
                }



                <div className={styles['footer']}>

                    {hasSeat &&
                        <p className={styles['seat-label']}><strong>{membershipCard.area} {
                            membershipCard.side === 'L' && 'Left'
                            || membershipCard.side === 'R' && 'Right'
                            || membershipCard.side === 'M' && 'Center'
                            || ''
                        } {membershipCard.row} {membershipCard.seat}</strong></p>
                        || <p></p>}

                    {membershipCard.hasNFCCard &&
                        <StatusLabel className={styles['edit-icon']}
                            text='+ NFC Card'
                            statusValue='NFCBought' />}
                    {ableToBuyNFC && !!!membershipCard.hasNFCCard && membershipCard.hasSeasonPass &&
                        <button
                            className={'btn-primary' + ' ' + styles['buy-nfc-button']}
                            onClick={() => showBuySeasonPassModal(true)}>
                            <StatusLabel className={styles['edit-icon']}
                                text='+NFC'
                                statusValue='NFCBuy' />
                        </button>}

                </div>

                {isLoading &&
                    <NewLoader className='' />
                }

            </div>

            {membershipCard.hasSeasonPass
                ? <div className={styles['season-pass-buyed']}>

                    <p className={styles['season-pass-buyed-label']}>
                        {txt.SeasonPassListTxt['seasonPassPurchased']}
                    </p>

                </div>
                : <button disabled={(allowBuy && hasSeat && membershipCard.customerName) ? undefined : true} className='btn-primary' onClick={() => showBuySeasonPassModal()}>{txt.SeasonPassListTxt['buySeasonPass']}</button>
            }
        </li>
    </>
}

const HoldedSeatCard = ({ memberEventInfo, id, area, side, row, seat }) => {
    const memberPageContext = useContext(MemberPageContext);
    const txt = getCurrentLanguageTexts();

    const openMembershipSellPage = () => {
        MySwal.fire({
            html: (
                <BuyMembershipsModalNew
                    eventData={memberEventInfo}
                    userData={memberPageContext.userData}
                    seat={{ area, side, row, seat }}
                />
            ),
            showConfirmButton: false,
            showCloseButton: true,
            customClass: "public-user-modal member-page-modal",
        });
    };

    const moveSeatToEmail = () => {
        MySwal.fire({
            title: txt.MoveHoldedSeatModalTxt['title'],
            html: (
                <MoveSeatModal
                    seatLabel={`${area}-${side}-${row}-${seat}`} />
            ),
            showConfirmButton: false,
            showCloseButton: true,
            customClass: "public-user-modal member-page-modal",
        });
    };

    return <li key={id} className={styles['membership-card']}>

        <div className={styles['holded-seat']} onClick={openMembershipSellPage}>
            <div>
                <p style={{ textAlign: 'center' }}>{txt.SeasonPassListTxt.youHaveReservedSeat}</p>
                <p style={{ textAlign: 'center', color: "gray" }}> <strong>{area} {
                    side === 'L' && 'Left'
                    || side === 'R' && 'Right'
                    || side === 'M' && 'Center'
                    || ''
                } {row} {seat}</strong></p>
            </div>
        </div>

        <button className={`btn-primary-new ${styles['replace-seat-button']}`} onClick={() => moveSeatToEmail()}>{txt.SeasonPassListTxt['moveAnotherEmail']}</button>
    </li>
}

export const MembershipCardContainer = ({ onSave, membershipCards, holdedSeats, currencyLabel, onConfirmBuySeasonPass, availablePoints, eventInfo, memberEventInfo, onConfirmBuyNfc, ableToBuyNFC, memberData, isDemoMode }) => {
    const txt = getCurrentLanguageTexts();

    const memberPageContext = useContext(MemberPageContext);

    const showTicketsPickerModal = () => {
        MySwal.fire({
            html: (
                <BuyMembershipsModalNew
                    eventData={memberEventInfo}
                    userData={memberPageContext.userData}
                />
            ),
            showConfirmButton: false,
            showCloseButton: true,
            customClass: "public-user-modal member-page-modal",
        });
    }

    return <>
        <ul className={styles['membership-cards-container']}>
            {<li key={9999} hidden={memberEventInfo.isSalesAvailable && !isDemoMode}>
                <button
                    className={styles['add-ticket-button']}
                    onClick={showTicketsPickerModal}>
                    {txt.buyMembershipTxt.buyTitle}
                </button>
            </li>}

            {membershipCards.map(m =>
                <MembershipCard
                    key={m.id}
                    onSave={onSave}
                    membershipCard={m}
                    currencyLabel={currencyLabel}
                    onConfirmBuySeasonPass={onConfirmBuySeasonPass}
                    availablePoints={availablePoints}
                    eventInfo={eventInfo}
                    onConfirmBuyNfc={onConfirmBuyNfc}
                    ableToBuyNFC={ableToBuyNFC}
                    holdedSeats={holdedSeats} />)}

            {!memberPageContext.userData.pendingToApprove
                && (holdedSeats.map(s => <HoldedSeatCard
                    {...s} memberEventInfo={memberEventInfo} />))
            }

        </ul>
    </>
}